import React, { useState } from "react";
import Button from "./ActionButton";
import { useNavigate } from "react-router";
import styled from "styled-components";
import { devices } from "../../Devices";
import { IonIcon } from "@ionic/react";
import { checkmark } from "ionicons/icons";
import Help from "./Help";
import { formatPlanPrice } from "../../services/Utils";

const Container = styled.div`
  --primary-orange: #d97706;
  --light-orange: #f59e0b;
  --dark-orange: #b45309;
  --text-main: #111111;
  --text-secondary: #555555;
  --bg-soft: #fafafa;

  gap: 2.4rem;
  padding-bottom: 2.4rem;

  .title {
    font-size: 3.5rem;
    line-height: 1.1;
    color: var(--text-main);
    text-align: center;

    @media ${devices.tablet} {
      font-size: 3rem;
    }
  }

  .plan-description {
    font-size: 1.6rem;
    color: var(--text-secondary);
    margin-bottom: 1.2rem;
    text-align: center;
  }

  .main-plans {
    width: 100%;
    max-width: 1040px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 2rem;
    align-items: stretch;

    @media ${devices.mobileL} {
      grid-template-columns: 1fr;
      gap: 1.6rem;
    }
  }

  .savings-section {
    width: 100%;
    max-width: 1120px;
    margin: 1.2rem auto 0;
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
  }

  .savings-title {
    margin: 0;
    color: var(--text-main);
    font-size: 2.2rem;
    text-align: center;
  }

  .secondary-plans {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 1.4rem;

    @media ${devices.laptop} {
      grid-template-columns: 1fr;
    }

    @media ${devices.mobileL} {
      gap: 1rem;
    }
  }
`;

const PlanCard = styled.div`
  background-color: #ffffff;
  border: 1px solid #ececec;
  border-radius: 12px;
  box-shadow: 0 10px 28px rgba(17, 17, 17, 0.06);
  padding: 2.1rem 1.8rem 1.8rem;
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100%;
  transition:
    transform 220ms ease,
    box-shadow 220ms ease,
    border-color 220ms ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 16px 34px rgba(17, 17, 17, 0.1);
    border-color: #f1d3b1;
  }

  &.highlighted {
    border: 2px solid #d97706;
    background-color: #fffaf4;
    box-shadow: 0 16px 36px rgba(217, 119, 6, 0.18);
  }

  &.compact {
    padding: 1.5rem;
    box-shadow: 0 5px 16px rgba(17, 17, 17, 0.05);

    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 9px 22px rgba(17, 17, 17, 0.08);
    }
  }

  .plan-days {
    display: inline-flex;
    align-self: center;
    margin-bottom: 1rem;
    color: #b45309;
    background: #fff7ed;
    border: 1px solid #f2d1ab;
    border-radius: 999px;
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: 0.05em;
    padding: 0.3rem 0.8rem;
  }

  .recommended-badge {
    display: inline-flex;
    align-self: center;
    margin-bottom: 1rem;
    background: #d97706;
    border: 1px solid #b45309;
    color: #ffffff;
    border-radius: 999px;
    font-size: 1.1rem;
    font-weight: 700;
    padding: 0.45rem 1rem;
  }

  .savings-badge {
    display: inline-flex;
    align-self: center;
    margin-bottom: 0.8rem;
    background: #fff7ed;
    border: 1px solid #f2d1ab;
    color: #b45309;
    border-radius: 999px;
    font-size: 1.05rem;
    font-weight: 700;
    padding: 0.3rem 0.75rem;
  }

  .plan-name {
    text-align: center;
    margin: 0 0 1.2rem;
    font-size: 2.6rem;
    line-height: 1.15;
    font-weight: 800;
    color: #111111;
  }

  .price {
    display: flex;
    align-items: baseline;
    justify-content: center;
    flex-wrap: wrap;
    font-size: 3.9rem;
    font-weight: 800;
    color: #d97706;
    text-align: center;
    margin-bottom: 1.4rem;
    line-height: 1.1;

    .decimals {
      font-size: 1.7rem;
      margin-right: 0.35rem;
    }

    .per-month {
      font-size: 1.5rem;
      font-weight: 600;
      color: #555555;
      margin-left: 0.35rem;
    }

    .price-total-label {
      display: block;
      width: 100%;
      margin-top: 0.5rem;
      color: #555555;
      font-size: 1.15rem;
      font-weight: 600;
    }
  }

  &.compact .plan-name {
    font-size: 2rem;
    margin-bottom: 0.8rem;
  }

  &.compact .price {
    font-size: 3rem;
    margin-bottom: 0.8rem;
  }

  .payment-note {
    margin: -0.3rem 0 1rem;
    color: #555555;
    font-size: 1.2rem;
    line-height: 1.4;
    text-align: center;
  }

  .divider {
    height: 1px;
    width: 100%;
    background-color: #efefef;
    margin-bottom: 1.3rem;
  }

  .feature-list {
    margin-bottom: 1.3rem;
    list-style: none;
    gap: 0.85rem;
    flex: 1;

    li {
      display: flex;
      gap: 0.85rem;
      align-items: flex-start;

      .icon {
        color: #d97706;
        min-width: 1.6rem;
        margin-top: 0.2rem;
      }
    }
  }

  .bonus-list {
    flex: none;
    margin: 0 0 0.8rem;
  }

  .feature {
    font-size: 1.4rem;
    color: #555555;
    line-height: 1.45;
  }

  .feature.bonus {
    color: #b45309;
    font-weight: 700;
    background: #fff7ed;
    border: 1px solid #f7d8b3;
    border-radius: 8px;
    padding: 0.45rem 0.6rem;
  }

  .benefits-toggle {
    align-self: center;
    margin: 0.2rem 0 1rem;
    padding: 0.4rem;
    border: 0;
    background: transparent;
    color: #9a4e00;
    font: inherit;
    font-size: 1.3rem;
    font-weight: 700;
    text-decoration: underline;
    text-underline-offset: 0.2rem;
    cursor: pointer;
  }

  .cta-wrapper {
    margin-top: auto;
    display: flex;
    .action-button {
      width: 100%;
      padding: 1.1rem 0.9rem;
      border-radius: 12px !important;
      font-size: 1.45rem;
      font-weight: 700;
      background-color: #d97706;
      color: #ffffff;

      &:hover {
        background-color: #b45309 !important;
        transform: translateY(-1px);
        transition: all 180ms ease-in-out;
      }
    }
  }

  &.highlighted .action-button {
    text-transform: none;
  }
`;

function PlanCardComponent({
  planName,
  lookupKey,
  features,
  highlighted = false,
  compact = false,
  badge,
  monthly = false,
  ctaText = "Escolher plano",
  daysInfo,
}) {
  const navigate = useNavigate();
  const [showBenefits, setShowBenefits] = useState(false);
  const price = formatPlanPrice(lookupKey);
  const bonusFeatures = features.filter((feature) =>
    feature.startsWith("Brinde:"),
  );
  const regularFeatures = features.filter(
    (feature) => !feature.startsWith("Brinde:"),
  );

  return (
    <PlanCard
      className={`${highlighted ? "highlighted" : ""} ${compact ? "compact" : ""}`}
    >
      {badge && (
        <span className={highlighted ? "recommended-badge" : "savings-badge"}>
          {badge}
        </span>
      )}
      {daysInfo && <span className="plan-days">{daysInfo}</span>}
      <h2 className="plan-name">{planName}</h2>
      <p className="price">
        <span className="decimals">R$</span>
        {price}
        {monthly ? (
          <span className="per-month">/mês</span>
        ) : (
          <span className="price-total-label">Valor total</span>
        )}
      </p>
      {!monthly && compact && (
        <p className="payment-note">Pagamento integral antecipado</p>
      )}
      <div className="divider" />
      {bonusFeatures.length > 0 && (
        <ul className="feature-list bonus-list flex direction-column">
          {bonusFeatures.map((feature) => (
            <li className="feature bonus" key={feature}>
              <IonIcon className="icon" icon={checkmark} /> {feature}
            </li>
          ))}
        </ul>
      )}
      {compact && (
        <button
          className="benefits-toggle"
          type="button"
          aria-expanded={showBenefits}
          onClick={() => setShowBenefits((visible) => !visible)}
        >
          {showBenefits ? "Ocultar benefícios" : "Ver benefícios"}
        </button>
      )}
      {(!compact || showBenefits) && (
        <ul className="feature-list flex direction-column">
          {regularFeatures.map((feature, idx) => (
            <li className="feature" key={idx}>
              <IonIcon className="icon" icon={checkmark} /> {feature}
            </li>
          ))}
        </ul>
      )}
      <div className="cta-wrapper">
        <Button
          text={ctaText}
          $fullWidth
          action={() => navigate(`/metodo-pagamento?lookup_key=${lookupKey}`)}
        />
      </div>
    </PlanCard>
  );
}

export default function Plans({ title }) {
  return (
    <Container className="flex direction-column">
      <Help />
      <div className="flex direction-column min-gap align">
        <h1 className="title">{title}</h1>
        <p className="plan-description">
          Temos planos para todos os momentos do seu negócio.
        </p>
      </div>
      <div className="main-plans">
        <PlanCardComponent
          planName="Premium Mensal"
          lookupKey="MONTHLY_PREMIUM_PLAN"
          highlighted
          monthly
          daysInfo="30 DIAS"
          badge="Recomendado"
          ctaText="QUERO ACESSO COMPLETO"
          features={[
            "Temas novos toda semana",
            "Monte em até 1 minuto com apenas uma lista de produtos",
            "Formato cartaz para impressão",
            "Formato para montagem de combos",
            "Formato para montar cestas básicas",
            "Diferentes grades de produtos",
            "Compartilhe direto para as redes",
            "Criações ilimitadas",
          ]}
        />
        <PlanCardComponent
          planName="Básico Mensal"
          lookupKey="MONTHLY_BASIC_PLAN"
          monthly
          daysInfo="30 DIAS"
          features={["Mais de 80 temas disponíveis", "3 encartes por dia"]}
        />
      </div>

      <section className="savings-section" aria-labelledby="savings-title">
        <h2 className="savings-title" id="savings-title">
          Quer economizar no Premium?
        </h2>
        <div className="secondary-plans">
          <PlanCardComponent
            planName="Premium Trimestral"
            lookupKey="TRIMESTER_PREMIUM_PLAN"
            compact
            daysInfo="90 DIAS"
            badge="8% de economia"
            features={[
              "Temas novos toda semana",
              "Monte em até 1 minuto com apenas uma lista de produtos",
              "Formato cartaz para impressão",
              "Formato para montagem de combos",
              "Formato para montar cestas básicas",
              "Diferentes grades de produtos",
              "Compartilhe direto para as redes",
              "Criações ilimitadas",
            ]}
          />
          <PlanCardComponent
            planName="Premium Semestral"
            lookupKey="SEMESTER_PREMIUM_PLAN"
            compact
            daysInfo="180 DIAS"
            badge="12% de economia"
            features={[
              "Temas novos toda semana",
              "Monte em até 1 minuto com apenas uma lista de produtos",
              "Formato cartaz para impressão",
              "Formato para montagem de combos",
              "Formato para montar cestas básicas",
              "Diferentes grades de produtos",
              "Compartilhe direto para as redes",
              "Criações ilimitadas",
              "Brinde: tema exclusivo",
            ]}
          />
          <PlanCardComponent
            planName="Premium Anual"
            lookupKey="YEARLY_PREMIUM_PLAN"
            compact
            daysInfo="360 DIAS"
            badge="17% de economia"
            features={[
              "Temas novos toda semana",
              "Monte em até 1 minuto com apenas uma lista de produtos",
              "Formato cartaz para impressão",
              "Formato para montagem de combos",
              "Formato para montar cestas básicas",
              "Diferentes grades de produtos",
              "Compartilhe direto para as redes",
              "Criações ilimitadas",
              "Brinde: 2 meses grátis",
              "Brinde: tema exclusivo",
              "Brinde: Vídeo oferta",
            ]}
          />
        </div>
      </section>
    </Container>
  );
}
